/**
 * One home for how a drone's live telemetry is read and judged.
 *
 * Before this module the same decisions were spelled out three-plus times with
 * different answers: battery colour thresholds in StripBoard, the old
 * TelemetryPanel and DockPanel, and three different staleness models
 * (StripBoard's 60s offline bucket, TelemetryPanel's 30s/60s stale/lost,
 * droneMapGeoJson's staleLevel). A fourth copy in the drone card would have
 * made that worse, so the thresholds live here and every surface reads them.
 *
 * The judgements are deliberately conservative about what the data can support:
 * fields the pipeline does not actually carry return null rather than a
 * plausible-looking number.
 */
import { haversineDistanceM } from './mission-planning';
import { EXCEPTION_COPY } from './copy';
import type { LiveDrone } from '@/hooks/useWebSocket';
import type { AlarmEntry } from '@/components/dashboard/alarmTypes';

// ---------------------------------------------------------------------------
// Staleness — carries forward the retired TelemetryPanel's thresholds, the
// most operationally sensible of the three models that existed.
// ---------------------------------------------------------------------------
export const STALE_AFTER_MS = 30_000;
export const LOST_AFTER_MS = 60_000;

export type LinkFreshness = 'live' | 'stale' | 'lost';

export function linkFreshness(lastSeenAt: string, now: number = Date.now()): LinkFreshness {
  const ageMs = now - new Date(lastSeenAt).getTime();
  if (Number.isNaN(ageMs)) return 'lost';
  if (ageMs > LOST_AFTER_MS) return 'lost';
  if (ageMs > STALE_AFTER_MS) return 'stale';
  return 'live';
}

export function ageSeconds(lastSeenAt: string, now: number = Date.now()): number {
  const ageMs = now - new Date(lastSeenAt).getTime();
  return Number.isNaN(ageMs) ? 0 : Math.max(0, Math.floor(ageMs / 1000));
}

/** Compact age for dense readouts: 12s / 4m / 2h. */
export function formatAge(lastSeenAt: string, now: number = Date.now()): string {
  const sec = ageSeconds(lastSeenAt, now);
  if (sec < 60) return `${sec}s`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m`;
  return `${Math.floor(sec / 3600)}h`;
}

/**
 * Is this aircraft's FLYING claim still current?
 *
 * THE rule for any surface that asserts an aircraft is in the air right now - a count, a green
 * badge, an "executing" state. `status` is written once per telemetry frame and then FROZEN:
 * nothing but the next frame ever rewrites a drone in the COP's map, and the staleness sweep
 * publishes an alert, not a frame. So `status === 'flying'` read raw means "the last thing it
 * said was flying", which after a lost link can be a claim from an hour ago - and since #338 that
 * claim is reachable at pad height, where a dead publisher is the ordinary end of a DJI sortie.
 *
 * `flying && not lost`, the ordering `CopDroneSearch.resultState` and `StripBoard.getSortBucket`
 * each already apply by hand: a lost link outranks the last status received, because the display
 * must not vouch for a state nothing has confirmed in a minute. The selection chip is the
 * deliberate STRICTER outlier - it withdraws the FLYING word at 30 s (`stale`), not 60 s, because
 * it is the surface an operator reads while deciding on THIS aircraft and it pairs the demotion
 * with the age. Converting the chip to this helper would silently relax it; do not.
 *
 * WHAT THIS IS NOT. It is not the bay launch veto - `bayAirborneFrom` (dock-status.ts) keeps a
 * lost link airborne when the last position was off the pad, because re-opening LAUNCH under an
 * aircraft that may genuinely be up costs an airframe, while a wrong green badge costs a glance.
 * Two rules on purpose; the cost asymmetry is different and each is argued where it lives. And it
 * is not "on the ground": a `false` here means "not currently confirmed flying", which includes
 * an aircraft that is up but dark - say OFFLINE for those, never LANDED.
 *
 * A caller in a render body only re-evaluates when something re-renders. Surfaces with their own
 * ticker (StripBoard, DroneMap's stalenessTick) converge within a second; a tickerless surface
 * converges on the next inbound frame from ANY drone, and freezes with the whole COP when the
 * last publisher dies. That ceiling is inherent to reading a clock in render - name it at the
 * call site rather than hiding a timer here.
 */
export function isCurrentlyFlying(
  drone: Pick<LiveDrone, 'status' | 'last_seen_at'>,
  now: number = Date.now(),
): boolean {
  return drone.status === 'flying' && linkFreshness(drone.last_seen_at, now) !== 'lost';
}

// ---------------------------------------------------------------------------
// Do we actually know where the aircraft is?
// ---------------------------------------------------------------------------

/**
 * The least a surface needs to answer "where is it, and is that still true".
 *
 * `LiveDrone` satisfies this structurally, so live tracks pass straight in
 * without a mapping step.
 */
export interface DroneTrack {
  latitude: number;
  longitude: number;
  last_seen_at: string;
}

/**
 * Whether a track can be pointed at, and when it cannot, why.
 *
 * Any control that moves the camera onto an aircraft has to ask this first. A
 * track existing is not the same as knowing where the airframe is: the position
 * can be absent (null island is the shape a missing fix arrives in — see the
 * same guard in DockLaunchOverlay's takeoff origin), or it can be real but too
 * old to point at. An idle simulated aircraft reaches that second state on its
 * own, because a bit-identical feed is dropped after 60s.
 */
export type CameraFix =
  | { usable: true; lat: number; lng: number }
  /** Nothing on this picture reports as that aircraft — it has never sent a
   *  frame on this connection, or a site filter excludes it. */
  | { usable: false; reason: 'no-track' }
  /** A track, but no position in it — no GPS lock, or a placeholder 0/0. */
  | { usable: false; reason: 'no-gps-fix' }
  /** A position, but older than LOST_AFTER_MS: it is where the aircraft WAS. */
  | { usable: false; reason: 'link-lost' };

/**
 * Resolve a live track to a camera target.
 *
 * `stale` (30-60s) still passes: a fix under a minute old is worth pointing at,
 * and the surface that shows it already flags the link as stale. `lost` does
 * not — flying the camera to a minute-old position asserts an aircraft is
 * somewhere nobody has confirmed it is.
 */
export function cameraFix(track: DroneTrack | null | undefined, now: number = Date.now()): CameraFix {
  if (!track) return { usable: false, reason: 'no-track' };
  const { latitude: lat, longitude: lng } = track;
  const plausible =
    Number.isFinite(lat) && Number.isFinite(lng)
    && Math.abs(lat) <= 90 && Math.abs(lng) <= 180
    && !(lat === 0 && lng === 0);
  if (!plausible) return { usable: false, reason: 'no-gps-fix' };
  if (linkFreshness(track.last_seen_at, now) === 'lost') return { usable: false, reason: 'link-lost' };
  return { usable: true, lat, lng };
}

// ---------------------------------------------------------------------------
// Level thresholds
// ---------------------------------------------------------------------------
export type Level = 'ok' | 'warn' | 'critical';

export const BATTERY_WARN_AT = 50;
export const BATTERY_CRITICAL_AT = 20;
export const SIGNAL_WARN_AT = 60;
export const SIGNAL_CRITICAL_AT = 30;
/** Below this the GPS fix is not trustworthy enough to fly on. */
export const MIN_HEALTHY_SATS = 8;

export function batteryLevel(percent: number | null): Level {
  // An unknown charge is not a healthy one. It reads 'warn' so the operator
  // sees something is off, without the red that means "this aircraft is about
  // to fall out of the sky" - the reading is missing, not critical.
  if (percent === null) return 'warn';
  if (percent > BATTERY_WARN_AT) return 'ok';
  if (percent > BATTERY_CRITICAL_AT) return 'warn';
  return 'critical';
}

export function signalLevel(percent: number): Level {
  if (percent > SIGNAL_WARN_AT) return 'ok';
  if (percent > SIGNAL_CRITICAL_AT) return 'warn';
  return 'critical';
}

// ---------------------------------------------------------------------------
// What the telemetry can and cannot tell us
// ---------------------------------------------------------------------------

/**
 * Battery percentage, or null when the source does not report one.
 *
 * RemoteID is a broadcast beacon: it carries position and identity but no
 * battery state, so the field arrives as 0. Rendering that as "0%" reads as a
 * flat battery on an aircraft that may be perfectly healthy — the long-standing
 * known issue this fixes.
 */
export function batteryPercent(drone: Pick<LiveDrone, 'battery_percent' | 'telemetry_source'>): number | null {
  if (drone.telemetry_source === 'remoteid') return null;
  if (typeof drone.battery_percent !== 'number' || Number.isNaN(drone.battery_percent)) return null;
  return drone.battery_percent;
}

/**
 * Link strength, or null with the reason it is unavailable.
 *
 * The MAVLink adapter publishes a hardcoded `signal_strength: 100` because
 * ArduPilot's SYS_STATUS does not give a usable RSSI over the current bridge.
 * Showing a constant 100% as a link metric would be worse than showing nothing,
 * especially now that MAVLink is the primary airframe.
 */
export function linkPercent(
  drone: Pick<LiveDrone, 'signal_strength' | 'telemetry_source'>,
): { percent: number | null; unavailableReason: string | null } {
  if (drone.telemetry_source === 'mavlink') {
    return { percent: null, unavailableReason: 'not reported over MAVLink' };
  }
  if (typeof drone.signal_strength !== 'number' || Number.isNaN(drone.signal_strength)) {
    return { percent: null, unavailableReason: 'not reported' };
  }
  return { percent: drone.signal_strength, unavailableReason: null };
}

/**
 * Straight-line distance from the drone to the dock it launched from, in metres,
 * or null when there is no dock to measure against.
 *
 * The dock is a stand-in for home: no home coordinate exists anywhere in the
 * telemetry pipeline today (the MAVLink bridge does not listen for
 * HOME_POSITION), so a hand-launched drone genuinely has no reference point and
 * gets null rather than a distance from something that is not its home.
 */
export function homeDistanceM(
  drone: Pick<LiveDrone, 'latitude' | 'longitude'>,
  dock: { latitude: number | null; longitude: number | null } | null | undefined,
): number | null {
  if (!dock || dock.latitude === null || dock.longitude === null) return null;
  if (typeof drone.latitude !== 'number' || typeof drone.longitude !== 'number') return null;
  return haversineDistanceM(
    { lat: drone.latitude, lng: drone.longitude },
    { lat: dock.latitude, lng: dock.longitude },
  );
}

export function formatDistance(metres: number): string {
  return metres < 1000 ? `${Math.round(metres)} m` : `${(metres / 1000).toFixed(2)} km`;
}

/** Below this, whole metres hide the answer the operator is actually asking. */
const NEAR_FIELD_M = 10;

/**
 * Distance formatted for the near field — "is the aircraft over its dock?".
 *
 * `formatDistance` rounds to whole metres, which is right for a transit leg and
 * useless on a launch pad: 1.4 m reads as "1 m" and anything under half a metre
 * reads as "0 m", which looks like missing data rather than a centred aircraft.
 * One decimal under 10 m, whole metres beyond.
 *
 * It does NOT claim "over the dock": GPS error alone is a metre or more beside a
 * metal dock, so the honest instrument is the number, and the operator's eyes
 * (or the dock camera) make the call.
 */
export function formatOffsetM(metres: number): string {
  return metres < NEAR_FIELD_M ? `${metres.toFixed(1)} m` : formatDistance(metres);
}

/**
 * Initial great-circle bearing from the drone to its home dock, in degrees true
 * (0–359), or null when there is no dock — same reference caveat as
 * `homeDistanceM`.
 *
 * This is the direction the operator would have to fly to get back, which a
 * distance alone does not give them.
 */
export function homeBearingDeg(
  drone: Pick<LiveDrone, 'latitude' | 'longitude'>,
  dock: { latitude: number | null; longitude: number | null } | null | undefined,
): number | null {
  if (!dock || dock.latitude === null || dock.longitude === null) return null;
  if (typeof drone.latitude !== 'number' || typeof drone.longitude !== 'number') return null;

  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const lat1 = toRad(drone.latitude);
  const lat2 = toRad(dock.latitude);
  const dLng = toRad(dock.longitude - drone.longitude);

  const y = Math.sin(dLng) * Math.cos(lat2);
  const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng);
  const deg = (Math.atan2(y, x) * 180) / Math.PI;
  return Math.round((deg + 360) % 360);
}

// ---------------------------------------------------------------------------
// Exceptions — what is wrong with this aircraft right now.
//
// Rendered only when non-empty: a healthy drone shows no strip at all rather
// than a row of green "OK"s, so anything visible is by definition something the
// operator needs to look at.
// ---------------------------------------------------------------------------
export type ExceptionKind = 'link' | 'battery' | 'gps' | 'alert';

export interface DroneException {
  kind: ExceptionKind;
  severity: 'warning' | 'critical';
  label: string;
}

const SEVERITY_RANK: Record<DroneException['severity'], number> = { critical: 0, warning: 1 };

// Ties break on the condition, never on arrival order, so the row that surfaces
// first is the same one every time this state recurs — the fixed priority
// sequence a crew alerting system is required to have, for the same reason the
// command bar's key positions are fixed: muscle memory.
//
// Link outranks battery deliberately. Once telemetry has gone stale every other
// number on the card is a stale number, battery included, so "what you are
// reading is old" has to be read before the readings are acted on.
const KIND_RANK: Record<ExceptionKind, number> = { link: 0, battery: 1, gps: 2, alert: 3 };

export function exceptionsFor(
  drone: LiveDrone,
  alerts: AlarmEntry[] = [],
  now: number = Date.now(),
): DroneException[] {
  const out: DroneException[] = [];

  const freshness = linkFreshness(drone.last_seen_at, now);
  if (freshness === 'lost') {
    out.push({ kind: 'link', severity: 'critical', label: EXCEPTION_COPY.noTelemetry({ age: formatAge(drone.last_seen_at, now) }) });
  } else if (freshness === 'stale') {
    out.push({ kind: 'link', severity: 'warning', label: EXCEPTION_COPY.telemetryOld({ age: formatAge(drone.last_seen_at, now) }) });
  }

  const battery = batteryPercent(drone);
  if (battery !== null) {
    const level = batteryLevel(battery);
    if (level === 'critical') {
      out.push({ kind: 'battery', severity: 'critical', label: EXCEPTION_COPY.batteryBelowReserve({ percent: battery }) });
    } else if (level === 'warn') {
      out.push({ kind: 'battery', severity: 'warning', label: EXCEPTION_COPY.batteryPlanRecovery({ percent: battery }) });
    }
  }

  // Satellites are reported by every source that reports a fix at all, but a
  // RemoteID beacon carries no GNSS health, so an absent count is not a fault.
  if (drone.telemetry_source !== 'remoteid' && typeof drone.satellites === 'number' && drone.satellites > 0
      && drone.satellites < MIN_HEALTHY_SATS) {
    out.push({ kind: 'gps', severity: 'warning', label: EXCEPTION_COPY.gpsWeak({ satellites: drone.satellites }) });
  }

  // One condition produces one row. The backend opens a *new* `alerts` row every
  // time a rule's cooldown lapses (`alert_rules.cooldown_minutes`, default 5), so
  // an aircraft that has sat below its battery threshold for twenty minutes
  // reaches this function as four identical rows. That is one condition, and
  // rendering it four times is the alarm flood the strip exists to prevent.
  //
  // `rule_id` is the condition; `id` is only one firing of it. Collapse on the
  // former and keep the most severe firing — a condition that has reached
  // critical is critical, however it started.
  const byCondition = new Map<string, DroneException>();
  for (const alert of alerts) {
    if (alert.drone_id !== drone.drone_id) continue;
    if (alert.status === 'resolved') continue;
    if (alert.severity === 'info') continue;

    // A deleted rule nulls the column (`ON DELETE SET NULL`). Keying those on
    // the title still collapses the repeat case; keying them all on `null` would
    // instead collapse unrelated conditions into one row and hide a fault.
    const key = alert.rule_id ?? `title:${alert.title.trim().toUpperCase()}`;
    const severity = alert.severity === 'critical' ? 'critical' : 'warning';
    const seen = byCondition.get(key);
    if (!seen || SEVERITY_RANK[severity] < SEVERITY_RANK[seen.severity]) {
      byCondition.set(key, { kind: 'alert', severity, label: alert.title.toUpperCase() });
    }
  }
  out.push(...byCondition.values());

  return out.sort(
    (a, b) =>
      SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity] || KIND_RANK[a.kind] - KIND_RANK[b.kind],
  );
}
